import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { FiEdit, FiPlus, FiTrash2 } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

import ConfirmModal from "../../components/ui/ConfirmModal";
import { useFeedback } from "./hooks/FeedbackContext";
import { useAllArticles } from "./hooks/useAllArticles";
import { useAllCategories } from "./hooks/useAllCategories";
import { useDeleteArticle } from "./hooks/useDeleteArticle";

import MultiSelectFilter from "./components/MultiSelectFilter";

import TableFilters from "./components/TableFilter";
import { STATUSES } from "./constants/enums";
import { useTableState } from "./hooks/useTableState";

export default function AdminArticles() {
  const navigate = useNavigate();
  const { loading, error, articles, refetch } = useAllArticles();
  const { categories } = useAllCategories();
  const { deleteArticle, loading: deleteLoading } = useDeleteArticle();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState<number | null>(null);
  const { showFeedback } = useFeedback();

  const enrichedArticles = useMemo(() => {
    if (!articles) return [];
    return articles.map((art) => ({
      ...art,
      tituloLabel: art?.traducciones?.[0]?.titulo || "",
      categoriaLabel:
        categories?.find((c) => c.categoriaId === art?.categoriaId)
          ?.nombreEspanol || String(art?.categoriaId),
      estadoLabel: STATUSES?.find((s) => s.value === art?.estado)?.label || art?.estado
    }));
  }, [articles, categories]);

  const {
    search,
    setSearch,
    filters,
    setSpecificFilter,
    resetFilters,
    hasActiveFilters,
    order,
    orderBy,
    handleRequestSort,
    processedData: filteredArticles,
  } = useTableState({
    data: enrichedArticles,
    initialSortKey: "tituloLabel",
    searchExtractor: (item) => item.tituloLabel,
  });

  const categoryOptions = useMemo(() => {
    return (
      categories?.map((c) => ({
        value: c.categoriaId,
        label: c.nombreEspanol,
      })) || []
    );
  }, [categories]);

  const handleDeleteClick = (id: number) => {
    setArticleToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (articleToDelete === null) return;
    try {
      await deleteArticle(articleToDelete);
      refetch();
      setDeleteDialogOpen(false);
      showFeedback("Artículo eliminado correctamente", "success");
    } catch (err: any) {
      setDeleteDialogOpen(false);
      showFeedback(
        err?.message || "Ocurrió un error al eliminar el artículo",
        "error",
      );
    } finally {
      setArticleToDelete(null);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/* HEADER */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          gap: { xs: 2, sm: 0 },
          mb: 4,
        }}
      >
        <Typography
          variant="h5"
          component="h1"
          sx={{ fontWeight: 600, color: "text.primary" }}
        >
          Listado de Artículos
        </Typography>
        <Button
          variant="contained"
          startIcon={<FiPlus />}
          onClick={() => navigate("/admin/articulos/new")}
          sx={{
            bgcolor: "primary.main",
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            padding: "8px 16px",
            "&:hover": { bgcolor: "primary.dark" },
          }}
        >
          Nuevo Artículo
        </Button>
      </Box>

      {/* FILTROS */}
      {!loading && !error && articles?.length > 0 && (
        <TableFilters
          search={search}
          onSearchChange={setSearch}
          onClear={resetFilters}
          hasActiveFilters={hasActiveFilters}
          searchPlaceholder="Buscar por título..."
        >
          <MultiSelectFilter
            label="Categoría"
            value={filters["categoriaId"] || []}
            options={categoryOptions}
            onChange={(vals) => setSpecificFilter("categoriaId", vals)}
          />
          <MultiSelectFilter
            label="Estado"
            value={filters["estado"] || []}
            options={STATUSES}
            onChange={(vals) => setSpecificFilter("estado", vals)}
          />
        </TableFilters>
      )}

      {/* CONTENT AREA */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "40vh",
          }}
        >
          <CircularProgress size={52} />
        </Box>
      ) : error ? (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography color="error" variant="h6">
            Error al cargar los artículos
          </Typography>
          <Button variant="contained" onClick={() => refetch()} sx={{ mt: 2 }}>
            Reintentar
          </Button>
        </Box>
      ) : filteredArticles.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            height: "20vh",
            alignItems: "center",
          }}
        >
          <Typography color="text.secondary">
            No se encontraron registros coincidentes.
          </Typography>
        </Box>
      ) : (
        <>
          {/* DESKTOP TABLE */}
          <Box sx={{ display: { xs: "none", md: "block" } }}>
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ borderRadius: "12px" }}
            >
              <Table sx={{ minWidth: 650, tableLayout: "fixed" }}>
                <TableHead sx={{ bgcolor: "background.default" }}>
                  <TableRow>
                    <TableCell sx={{ width: "45%" }}>
                      <TableSortLabel
                        active={orderBy === "tituloLabel" && order !== null}
                        direction={
                          orderBy === "tituloLabel" && order ? order : "asc"
                        }
                        onClick={() => handleRequestSort("tituloLabel")}
                      >
                        TÍTULO
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ width: "15%" }}>
                      <TableSortLabel
                        active={orderBy === "categoriaLabel" && order !== null}
                        direction={
                          orderBy === "categoriaLabel" && order ? order : "asc"
                        }
                        onClick={() => handleRequestSort("categoriaLabel")}
                      >
                        CATEGORÍA
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ width: "15%" }}>
                      <TableSortLabel
                        active={orderBy === "estadoLabel" && order !== null}
                        direction={
                          orderBy === "estadoLabel" && order ? order : "asc"
                        }
                        onClick={() => handleRequestSort("estadoLabel")}
                      >
                        ESTADO
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ width: "15%" }} align="right">ACCIONES</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredArticles.map((article) => (
                    <TableRow
                      key={article.articuloId}
                      sx={{ "&:hover": { bgcolor: "background.default" } }}
                    >
                      <TableCell sx={{ fontWeight: 500 }}>
                        {article.tituloLabel}
                      </TableCell>
                      <TableCell
                        sx={{ color: "primary.main", fontWeight: 500 }}
                      >
                        {article.categoriaLabel}
                      </TableCell>
                      <TableCell>{article.estadoLabel}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() =>
                            navigate(
                              `/admin/articulos/edit/${article.articuloId}`,
                            )
                          }
                          sx={{ mr: 1 }}
                        >
                          <FiEdit />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(article.articuloId)}
                        >
                          <FiTrash2 />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* MOBILE CARDS */}
          <Box sx={{ display: { xs: "grid", md: "none" }, gap: 2 }}>
            {filteredArticles.map((article) => (
              <Paper
                key={article.articuloId}
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: "12px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 600, textTransform: "uppercase" }}
                >
                  Título
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {article.tituloLabel}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 600, textTransform: "uppercase" }}
                >
                  Categoría
                </Typography>
                <Typography variant="body2" sx={{ color: "primary.main" }}>
                  {article.categoriaLabel}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 600, textTransform: "uppercase" }}
                >
                  Estado
                </Typography>
                <Typography variant="body2">{article.estado}</Typography>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    mt: 1,
                    pt: 1,
                    borderTop: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() =>
                      navigate(`/admin/articulos/edit/${article.articuloId}`)
                    }
                    sx={{ mr: 1 }}
                  >
                    <FiEdit />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteClick(article.articuloId)}
                  >
                    <FiTrash2 />
                  </IconButton>
                </Box>
              </Paper>
            ))}
          </Box>

          <ConfirmModal
            open={deleteDialogOpen}
            title="Eliminar Artículo"
            content="¿Estás seguro de que deseas eliminar este artículo? Esta acción no se puede deshacer."
            confirmText="Eliminar"
            cancelText="Cancelar"
            onConfirm={handleDeleteConfirm}
            onCancel={() => setDeleteDialogOpen(false)}
            loading={deleteLoading}
          />
        </>
      )}
    </Box>
  );
}
